import { ReactNode, useId, createContext, useContext, useState, forwardRef } from "react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO } from "date-fns";
import { es as esLocale } from "date-fns/locale";
import { CalendarIcon, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Sheet, SheetContent, SheetTitle, SheetDescription, SheetHeader,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { Loader2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

/** Localized status label. Falls back to raw status key. */
export function useStatusLabel() {
  const { t } = useI18n();
  return (status: string | null | undefined): string => {
    if (!status) return "—";
    const map = (t as any)?.os?.status ?? {};
    return map[status] ?? status;
  };
}

/** Shadcn-based date picker that auto-closes on select. Value is ISO yyyy-MM-dd string. */
export function DatePicker({
  value,
  onChange,
  placeholder,
  className,
  disabledDate,
}: {
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  disabledDate?: (date: Date) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const date = value ? parseISO(value) : undefined;
  const { t, lang } = useI18n();
  const dfnsLocale = lang === "es" ? esLocale : undefined;
  const ph = placeholder ?? (t as any)?.os?.common?.pickDate ?? "Pick a date";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground", className)}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "PPP", { locale: dfnsLocale }) : <span>{ph}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          locale={dfnsLocale}
          disabled={disabledDate}
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
      </PopoverContent>
    </Popover>
  );
}

/** Page-level fade/slide used by OS routes. */
export function OSPageFade({ children, k }: { children: ReactNode; k: string }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={k}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.28, ease: [0.25, 1, 0.5, 1] }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/** Animated table body — wraps children in <AnimatePresence>. Children must be <MotionTr>. */
export function AnimatedTBody({ children }: { children: ReactNode }) {
  return (
    <tbody>
      <AnimatePresence initial={false}>{children}</AnimatePresence>
    </tbody>
  );
}

/** Drop-in <tr> with layout + enter/exit animations. Pass a stable `key`. */
export const MotionTr = motion.tr;
export const motionRowProps = {
  layout: true,
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.22, ease: [0.25, 1, 0.5, 1] as [number, number, number, number] },
};

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-6">
      <div className="min-w-0">
        <h1 className="text-2xl font-display tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          {actions}
        </div>
      )}
    </div>
  );
}

export function PagePadding({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("p-4 md:p-6 lg:p-8 max-w-7xl mx-auto w-full", className)}>{children}</div>;
}

export function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 text-3xl font-display tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon: Icon,
  compact,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: LucideIcon;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="py-8 px-4 text-center flex flex-col items-center gap-2">
        {Icon && (
          <div className="h-9 w-9 rounded-full bg-muted/60 grid place-items-center text-muted-foreground">
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </div>
        )}
        <div className="text-xs font-medium text-foreground">{title}</div>
        {body && <div className="text-[11px] text-muted-foreground max-w-[28ch] leading-snug">{body}</div>}
        {action && <div className="mt-1">{action}</div>}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-dashed bg-card/40 p-10 text-center">
      {Icon && (
        <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-muted/60 grid place-items-center text-muted-foreground">
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
      )}
      <div className="text-base font-medium">{title}</div>
      {body && <div className="text-sm text-muted-foreground mt-1">{body}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function statusBadge(status: string) {
  const map: Record<string, string> = {
    prospect: "bg-muted text-muted-foreground",
    active: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    inactive: "bg-muted text-muted-foreground",
    discovery: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    in_progress: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    on_hold: "bg-muted text-muted-foreground",
    completed: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    archived: "bg-muted text-muted-foreground",
    draft: "bg-muted text-muted-foreground",
    sent: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    accepted: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    rejected: "bg-rose-500/10 text-rose-700 dark:text-rose-400",
    expired: "bg-muted text-muted-foreground",
    paid: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    partial: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    overdue: "bg-rose-500/10 text-rose-700 dark:text-rose-400",
    void: "bg-muted text-muted-foreground",
    todo: "bg-muted text-muted-foreground",
    review: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    uat: "bg-teal-500/10 text-teal-700 dark:text-teal-400",
    done: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    pending: "bg-muted text-muted-foreground",
  };
  return map[status] ?? "bg-muted text-muted-foreground";
}

export function formatMoney(n: number | string | null | undefined, currency = "USD") {
  const num = typeof n === "string" ? parseFloat(n) : n ?? 0;
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(num || 0);
}

export function formatDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString();
}

/**
 * Long, human-friendly date: "12 de mayo, 2026" (es) / "May 12, 2026" (en).
 * Locale is taken from the live <html lang> attribute set by I18nProvider.
 */
export function formatDateLong(d: string | null | undefined, lang?: "es" | "en") {
  if (!d) return "—";
  const l = lang
    ?? ((typeof document !== "undefined" && document.documentElement.lang === "es") ? "es" : "en");
  if (l === "es") {
    const date = new Date(d);
    const months = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const monthCap = month.charAt(0).toUpperCase() + month.slice(1);
    return `${day} de ${monthCap}, ${date.getFullYear()}`;
  }
  return new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
}

/* ────────────────────────────────────────────────────────────────────────────
   PersonAvatar — rectangular 2:3 portrait avatar used across the OS.
   Replaces circular avatars to match the talent profile aesthetic.
   ──────────────────────────────────────────────────────────────────────────── */
const PERSON_AVATAR_SIZES = {
  xs: { box: "w-5", text: "text-[9px]", radius: "rounded-[3px]" },
  sm: { box: "w-6", text: "text-[10px]", radius: "rounded-[4px]" },
  md: { box: "w-8", text: "text-xs", radius: "rounded-md" },
  lg: { box: "w-12", text: "text-sm", radius: "rounded-md" },
  xl: { box: "w-24", text: "text-xl", radius: "rounded-lg" },
} as const;

export function PersonAvatar({
  src,
  alt,
  name,
  size = "md",
  className,
  color,
}: {
  src?: string | null;
  alt?: string;
  name?: string | null;
  size?: keyof typeof PERSON_AVATAR_SIZES;
  className?: string;
  color?: string | null;
}) {
  const s = PERSON_AVATAR_SIZES[size];
  const initials = (() => {
    const raw = (name ?? alt ?? "").trim();
    if (!raw) return "X";
    return (
      raw
        .split(/\s+/)
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "X"
    );
  })();
  return (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden bg-muted ring-1 ring-border aspect-[2/3]",
        s.box,
        s.radius,
        className,
      )}
    >
      {src ? (
        <img src={src} alt={alt ?? name ?? ""} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center font-medium",
            color ? "text-white" : "text-muted-foreground",
            s.text,
          )}
          style={color ? { backgroundColor: color } : undefined}
        >
          {initials}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Field — Label + small gray description + control. Use across every form.
   ──────────────────────────────────────────────────────────────────────────── */
export function Field({
  label,
  hint,
  required,
  htmlFor,
  className,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={htmlFor} className="text-sm">
        {label}
        {required && <span className="text-muted-foreground"> *</span>}
      </Label>
      {hint && <p className="text-xs text-muted-foreground leading-snug">{hint}</p>}
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   OSTabs — Radix Tabs + framer-motion pill (shared layoutId) + fade content.
   ──────────────────────────────────────────────────────────────────────────── */
type TabsCtxValue = { active: string; pillId: string };
const TabsCtx = createContext<TabsCtxValue | null>(null);

export function OSTabs({
  value,
  defaultValue,
  onValueChange,
  children,
  className,
}: {
  value?: string;
  defaultValue?: string;
  onValueChange?: (v: string) => void;
  children: ReactNode;
  className?: string;
}) {
  const [internal, setInternal] = useState<string>(defaultValue ?? "");
  const isControlled = value !== undefined;
  const active = isControlled ? (value as string) : internal;
  const pillId = useId();
  const handle = (v: string) => {
    if (!isControlled) setInternal(v);
    onValueChange?.(v);
  };
  return (
    <TabsCtx.Provider value={{ active, pillId }}>
      <TabsPrimitive.Root
        value={active}
        defaultValue={defaultValue}
        onValueChange={handle}
        className={className}
      >
        {children}
      </TabsPrimitive.Root>
    </TabsCtx.Provider>
  );
}

export const OSTabsList = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "relative inline-flex h-10 max-w-full items-center justify-center overflow-x-auto rounded-lg bg-muted p-1 text-muted-foreground [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      className,
    )}
    {...props}
  />
));
OSTabsList.displayName = "OSTabsList";

export const OSTabsTrigger = forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ value, className, children, ...props }, ref) => {
  const ctx = useContext(TabsCtx);
  const isActive = ctx?.active === value;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      value={value as string}
      className={cn(
        "relative inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
        "text-muted-foreground data-[state=active]:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {isActive && ctx && (
        <motion.span
          layoutId={ctx.pillId}
          className="absolute inset-0 z-0 rounded-md bg-background shadow-sm border border-border/60"
          transition={{ type: "spring", stiffness: 380, damping: 32 }}
        />
      )}
      <span className="relative z-10 inline-flex items-center">{children}</span>
    </TabsPrimitive.Trigger>
  );
});
OSTabsTrigger.displayName = "OSTabsTrigger";

export const OSTabsContent = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ value, className, children, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    value={value as string}
    className={cn("mt-4 focus-visible:outline-none")}
    {...props}
  >
    <motion.div
      key={String(value)}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0.25, 1, 0.5, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  </TabsPrimitive.Content>
));
OSTabsContent.displayName = "OSTabsContent";

/* ────────────────────────────────────────────────────────────────────────────
   OSSheet — Header (title + description + divider) · scrollable body · sticky footer
   Header reserves space for the close button. Optional top-right badge slot.
   ──────────────────────────────────────────────────────────────────────────── */
const sheetSizes = {
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-xl",
  xl: "sm:max-w-2xl",
} as const;

export function OSSheet({
  open,
  onOpenChange,
  side = "right",
  size = "lg",
  title,
  description,
  badge,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  side?: "right" | "left" | "top" | "bottom";
  size?: keyof typeof sheetSizes;
  title: string;
  description?: string;
  badge?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        className={cn("w-full p-0 flex flex-col gap-0 outline-none focus:outline-none focus-visible:outline-none", sheetSizes[size])}
      >
        <SheetHeader className="px-6 pt-5 pb-4 pr-14 space-y-1 text-left shrink-0">
          <div className="flex items-start gap-3">
            <SheetTitle className="text-base font-semibold tracking-tight flex-1">
              {title}
            </SheetTitle>
            {badge && <div className="shrink-0 mr-2">{badge}</div>}
          </div>
          {description && (
            <SheetDescription className="text-xs text-muted-foreground leading-snug">
              {description}
            </SheetDescription>
          )}
        </SheetHeader>
        <div className="border-t border-border shrink-0" />
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <>
            <div className="border-t border-border shrink-0" />
            <div className="px-6 py-3 bg-background shrink-0">{footer}</div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* Footer with prominence: [destructive · cancel · primary] right-aligned. */
export function OSSheetFooter({
  primary,
  onCancel,
  cancelLabel,
  destructive,
  className,
}: {
  primary?: ReactNode;
  onCancel?: () => void;
  cancelLabel?: string;
  destructive?: ReactNode;
  className?: string;
}) {
  const { t } = useI18n();
  const label = cancelLabel ?? (t as any)?.os?.sheet?.cancel ?? "Cancel";
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {destructive ? <div className="mr-auto">{destructive}</div> : <div className="mr-auto" />}
      {onCancel && (
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {label}
        </Button>
      )}
      {primary}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   ConfirmDelete — context-aware confirmation dialog. Optional type-to-confirm
   for high-impact deletions.
   ──────────────────────────────────────────────────────────────────────────── */
export function ConfirmDelete({
  trigger,
  title,
  description,
  confirmLabel,
  requireType,
  onConfirm,
}: {
  trigger: ReactNode;
  title?: string;
  description: ReactNode;
  confirmLabel?: string;
  /** If set, user must type this exact word to enable the destructive action. */
  requireType?: string;
  onConfirm: () => void | Promise<void>;
}) {
  const { t } = useI18n();
  const cd = (t as any)?.os?.confirmDelete ?? {};
  const titleText = title ?? cd.title ?? "Delete this item?";
  const confirmText = confirmLabel ?? cd.confirm ?? "Delete";
  const cancelText = cd.cancel ?? "Cancel";
  const typePrompt = cd.typeToConfirm ?? "Type {word} to confirm this action.";
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const canConfirm = !requireType || typed.trim() === requireType;
  const reset = () => {
    setTyped("");
    setBusy(false);
  };
  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{titleText}</AlertDialogTitle>
          <AlertDialogDescription className="text-sm">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {requireType && (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">
              {typePrompt.split("{word}")[0]}
              <span className="font-mono font-semibold text-foreground">{requireType}</span>
              {typePrompt.split("{word}")[1] ?? ""}
            </p>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={requireType}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            disabled={!canConfirm || busy}
            onClick={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await onConfirm();
                setOpen(false);
                reset();
              } catch {
                setBusy(false);
              }
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
/* ────────────────────────────────────────────────────────────────────────────
   OSSheetMount — keeps a detail Sheet mounted long enough to play its
   close animation. Use this whenever a Sheet's "open" state lives in a
   parent that would otherwise unmount the Sheet on close.

   Pattern (REQUIRED for every new detail Sheet in Smart Canvas OS):

     <OSSheetMount value={selected} onClose={() => setSelected(null)}>
       {(item, open, onOpenChange) => (
         <MyDetailSheet item={item} open={open} onOpenChange={onOpenChange} />
       )}
     </OSSheetMount>

   The child receives the LAST non-null value, so the Sheet can keep
   rendering its data while it slides out.
   ──────────────────────────────────────────────────────────────────────────── */
import { useEffect as _useEffect, useRef as _useRef, useState as _useState } from "react";

export function OSSheetMount<T>({
  value,
  onClose,
  exitMs = 350,
  children,
}: {
  value: T | null | undefined;
  onClose: () => void;
  exitMs?: number;
  children: (item: T, open: boolean, onOpenChange: (o: boolean) => void) => ReactNode;
}) {
  const [last, setLast] = _useState<T | null | undefined>(value);
  const timer = _useRef<number | null>(null);

  _useEffect(() => {
    if (value != null) {
      if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
      setLast(value);
    } else if (last != null) {
      timer.current = window.setTimeout(() => { setLast(null); timer.current = null; }, exitMs);
    }
    return () => { if (timer.current) { window.clearTimeout(timer.current); timer.current = null; } };
  }, [value, exitMs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (last == null) return null;
  const open = value != null;
  return <>{children(last, open, (o) => { if (!o) onClose(); })}</>;
}
